import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSending(false);
    toast.success("Message sent! We'll get back to you within 24 hours.");
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  return (
    <PublicLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0A183D] to-[#1B3FA0] py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-3">Get in Touch</p>
            <h1 className="font-heading text-5xl font-bold text-white uppercase">Contact Us</h1>
            <p className="text-blue-200 mt-4">Have questions? We're here to help 24/7.</p>
          </div>
        </div>

        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="space-y-6">
              {[
                { icon: MapPin, title: "Our Office", lines: ["Sendtoo HQ", "Bandra Kurla Complex", "Mumbai, Maharashtra 400051"] },
                { icon: Phone, title: "Phone", lines: ["+91 1800 SENDTOO", "+91 22 4000 1234"] },
                { icon: Mail, title: "Email", lines: ["support@sendtoo.com", "business@sendtoo.com"] },
                { icon: Clock, title: "Working Hours", lines: ["Mon–Sat: 9am – 8pm", "Sunday: 10am – 5pm"] },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="flex gap-4 bg-white p-5 rounded-lg border border-slate-100"
                >
                  <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-brand-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800 text-sm mb-1">{item.title}</h4>
                    {item.lines.map((line, j) => <p key={j} className="text-xs text-slate-500">{line}</p>)}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm">
                <h2 className="font-heading font-semibold text-2xl text-slate-800 uppercase mb-6">Send us a Message</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {[
                    { name: "name", label: "Full Name", placeholder: "Your name" },
                    { name: "email", label: "Email Address", placeholder: "you@email.com" },
                    { name: "phone", label: "Phone Number", placeholder: "+91 9876543210" },
                    { name: "subject", label: "Subject", placeholder: "How can we help?" },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{field.label}</label>
                      <input
                        name={field.name}
                        value={form[field.name]}
                        onChange={(e) => setForm({ ...form, [e.target.name]: e.target.value })}
                        placeholder={field.placeholder}
                        data-testid={`contact-${field.name}`}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                  ))}
                </div>
                <div className="mb-6">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us more about your query..."
                    rows={5}
                    data-testid="contact-message"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="w-full py-3 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 uppercase tracking-wide"
                >
                  <Send className="w-4 h-4" />
                  {sending ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
